import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Radio } from 'antd';
import { toast } from 'react-toastify';
import GenericTable from './GenericTable';
import DeleteModal from '../modals/DeleteModal';
import Spinner from '../elements/Spinner/Spinner';
import HomeCategoryOrder from '../products/HomeCategoryOrder';
import categoryApi from '../../api/category-api';
import { formatBooleanAsPill } from '../../utils/formatBoolean';
import styles from '../../styles/webshopAndProducts.module.scss';

const CategoriesTable = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('active'); // 'active' | 'hidden'
  const [hideTarget, setHideTarget] = useState(null);
  const [hiding, setHiding] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await categoryApi.getAll(view === 'hidden');
    if (success) {
      setCategories(data);
    } else {
      toast.error(message || 'Greška pri učitavanju kategorija');
    }
    setLoading(false);
  }, [view]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleRestore = async (row) => {
    const { success, message } = await categoryApi.restore(row._id);
    if (success) {
      toast.success('Kategorija je uspešno vraćena');
      loadCategories();
    } else {
      toast.error(message || 'Greška pri vraćanju kategorije');
    }
  };

  const handleConfirmHide = async () => {
    if (!hideTarget) return;
    setHiding(true);
    const { success, message } = await categoryApi.hide(hideTarget._id);
    if (success) {
      toast.success('Kategorija je uspešno sakrivena');
      setHideTarget(null);
      loadCategories();
    } else {
      toast.error(message || 'Greška pri sakrivanju kategorije');
    }
    setHiding(false);
  };

  const columns = [
    {
      key: 'image',
      label: '',
      render: (val, row) =>
        row.image?.src ? (
          <img
            src={row.image.src}
            alt={row.name}
            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
          />
        ) : null,
    },
    { key: 'name', label: 'Naziv' },
    { key: 'slug', label: 'Slug' },
    {
      key: 'showOnHome',
      label: 'Na početnoj',
      render: (val) => formatBooleanAsPill(val),
    },
    {
      key: 'isPackages',
      label: 'Paketi',
      render: (val) => formatBooleanAsPill(val),
    },
  ];

  const actions = [
    {
      key: 'edit',
      label: 'Uredi kategoriju',
      icon: <img src="/assets/icons/edit-icon.svg" alt="Uredi" />,
      onClick: (row) => navigate(`/proizvodi/kategorije/izmeni/${row._id}`),
    },
    {
      key: 'hide',
      label: 'Sakrij kategoriju',
      icon: <img src="/assets/icons/delete.svg" alt="Sakrij" />,
      onClick: (row) => setHideTarget(row),
      visible: (row) => !row.isDeleted,
      className: 'delete',
      danger: true,
    },
    {
      key: 'restore',
      label: 'Vrati kategoriju',
      icon: <img src="/assets/icons/verification.svg" alt="Vrati" />,
      onClick: handleRestore,
      visible: (row) => !!row.isDeleted,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          width: '100%',
        }}
      >
        <Radio.Group value={view} onChange={(e) => setView(e.target.value)} optionType="button">
          <Radio.Button value="active">Aktivne</Radio.Button>
          <Radio.Button value="hidden">Sakrivene</Radio.Button>
        </Radio.Group>
        <Button
          className="btn-primary"
          type="primary"
          onClick={() => navigate('/proizvodi/kategorije/dodaj')}
        >
          + Dodaj kategoriju
        </Button>
      </div>

      {!loading && view === 'active' && (
        <HomeCategoryOrder categories={categories} onSaved={loadCategories} />
      )}

      {loading ? (
        <Spinner size="lg" />
      ) : (
        <GenericTable
          data={categories}
          columns={columns}
          actions={actions}
          rowClassName={(row) => (row.isDeleted ? styles.deletedRow : '')}
        />
      )}

      <DeleteModal
        visible={!!hideTarget}
        onDelete={handleConfirmHide}
        onCancel={() => setHideTarget(null)}
        loading={hiding}
        title="Sakrivanje kategorije"
        message={`Da li ste sigurni da želite da sakrijete kategoriju "${hideTarget?.name}"?`}
        buttonText="Sakrij kategoriju"
        cancelText="Odustani"
      />
    </div>
  );
};

export default CategoriesTable;
